import {
  required, min, max as maxRule, numeric, confirmed,
} from 'vee-validate/dist/rules';
import { extend } from 'vee-validate';
import { isSameDay } from './dates';
import { RepositoryFactory } from '../repositories/RepositoryFactory';

const shop = RepositoryFactory.get('user');

extend('required', {
  ...required,
  message: 'To pole jest wymagane',
});
extend('requiredRegistration', {
  ...required,
  message: 'Wybierz jedną z dostępnych opcji',
});

extend('min', {
  ...min,
  message: 'Hasło jest za krótkie. Powinno mieć co najmniej 8 znaków',
});

extend('max', {
  ...maxRule,
  message(field, { length }) {
    return `Pole może zawierać maksymalnie ${length} znaków`;
  },
});

extend('numeric', {
  ...numeric,
  message: 'Pole może zawierać tylko liczby',
});

extend('password', {
  validate: (value, { other }) => value === other,
  message: 'Hasła muszą być takie same',
  params: [{ name: 'other', isTarget: true }],
});

extend('chargeDate', {
  validate: (value, { other }) => {
    const chargeDate = new Date(value);
    const deliveryDate = new Date(other);
    return chargeDate > deliveryDate;
  },
  message: 'Ze względów bezpieczeństwa data doładowania karty powinna wypadać min. 1 dzień po dacie dostawy',
  params: [{ name: 'other', isTarget: true }],
});

extend('chargeTime', {
  validate: (value, { other, earliestAllowed }) => {
    if (!value || !other) return true;

    const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
    const match = String(value).match(timePattern);
    if (!match) return false;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (minutes % 15 !== 0) return false;

    const now = new Date();
    const selectedDeliveryDate = new Date(other);
    const isDeliveryToday = isSameDay(selectedDeliveryDate, now);
    if (!isDeliveryToday) return true;

    const earliestAllowedDateTime = new Date(Number(earliestAllowed));
    if (Number.isNaN(earliestAllowedDateTime.getTime())) {
      // eslint-disable-next-line no-console
      console.error('chargeTime validator requires a valid earliestAllowed parameter.');
      return false;
    }

    const selectedDateTime = new Date(now);
    selectedDateTime.setHours(hours, minutes, 0, 0);

    return selectedDateTime >= earliestAllowedDateTime;
  },
  message: 'Podaj poprawną godzinę',
  params: [{ name: 'other', isTarget: true }, { name: 'earliestAllowed' }],
});

extend('email', {
  validate: (value) => {
    const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/igm;
    return emailPattern.test(value);
  },
  message: 'Podaj poprawny adres email',
});

extend('confirmed', {
  ...confirmed,
  message: 'Podane adresy email muszą być takie same',
});

extend('passwordStrength', {
  validate: (value) => {
    const passPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[.,_!?#=+\-*&%$@:])[A-Za-z\d.,_!?#=+\-*&%$@:]{9,}$/;
    return passPattern.test(value);
  },
  message: 'Hasło powinno mieć co najmniej 9 znaków, jedną małą literę, jedną wielką literę, jedną cyfrę i jeden znak specjalny',
});

extend('nip', {
  validate: (value) => {
    const nipPattern = /^[0-9]{10}$/;
    return nipPattern.test(value);
  },
  message: 'Podaj poprawny numer NIP',
});

extend('zipCode', {
  validate: (value) => {
    const kodPattern = /\d{2}-\d{3}/;
    return kodPattern.test(value);
  },
  message: 'Podaj poprawny kod pocztowy (00-000)',
});

extend('phone', {
  validate: (value) => {
    const phonePattern = /\d{9}/;
    return phonePattern.test(value);
  },
  message: 'Podaj poprawny numer telefonu',
});

extend('accountNumber', {
  validate: (value) => {
    const testValue = value.replace(/-/g, '');
    const bankNumberPatter = /\d{26}/;
    return bankNumberPatter.test(testValue);
  },
  message: 'Wpisz poprawny numer rachunku bankowego',
});
extend('promoQuantity', {
  params: ['max'], // list of parameter names
  validate(value, { max }) {
    if (max[1] !== 'MRK') return true;
    return Number(value) <= max[0];
  },
  message: 'Liczba pakietów przekracza liczbę sztuk',
});

extend('confectionServiceProduct', {
  params: ['productAmountToCompare', 'parentProductName'],
  validate(value, { productAmountToCompare }) {
    return value <= Number(productAmountToCompare);
  },
  // eslint-disable-next-line no-undef
  message(field, { parentProductName }) {
    return `Nie można zamówić bez: ${parentProductName}`;
  },
});

extend('confectionDependencyMet', {
  params: ['errorMessage'],
  validate(value, { errorMessage }) {
    return !errorMessage;
  },
  message(field, { errorMessage }) {
    return errorMessage;
  },
  computesRequired: true,
});

extend('mutuallyExclusive', {
  params: ['otherProductQuantity', 'otherProductName'],
  validate(value, { otherProductQuantity }) {
    return !(value > 0 && Number(otherProductQuantity) > 0);
  },
  // eslint-disable-next-line no-undef
  message(field, { otherProductName }) {
    return `Nie można zamówić z: ${otherProductName}`;
  },
});

extend('confectionServiceProductNumber', {
  params: ['productAmountToCompare', 'parentProductName'],
  validate(value, { productAmountToCompare }) {
    return value <= Number(productAmountToCompare);
  },
  // eslint-disable-next-line no-undef
  message(field, { parentProductName }) {
    return `Liczba nie może być wyższa niż liczba zamówionych${parentProductName !== '' ? `: ${parentProductName}` : ' produktów'}`;
  },
});

extend('confectionMaxBaseOnProducts', {
  params: ['productAmountToCompare'],
  validate(value, { productAmountToCompare }) {
    return value <= Number(productAmountToCompare);
  },
  // eslint-disable-next-line no-undef
  message: 'Liczba nie może być wyższa niż liczba zamówionych produktów',
});

extend('asyncRule', {
  validate(value) {
    return new Promise((resolve) => {
      shop.checkUsername(value)
        .then((response) => {
          resolve(!response.data);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log(error.message);
        });
    });
  },
  message: 'Podana nazwa użytkownika jest zajęta.', // the error message
});
